import { initPersistedCalls } from '@/persistedQueue';
import { sneakyPromise } from '@/store/profiling';

let ipcRenderer;
if (window.APP_TRON) {
  ipcRenderer = window.APP_TRON.ipcRenderer;
}

const main = {};

const saveData = ({
  data, dataId, dbName, timestamp,
}) => {
  if (dataId.length > 1000) {
    console.warn('Cache key is too long, it may cause issues with storage. Skipping persisting');
    return;
  }

  if (window.APP_TRON) {
    const levelData = {
      id: dataId,
      data: JSON.stringify(data),
      timestamp,
    };
    window.APP_TRON.putLevelDBData(dbName, levelData).then(result => result)
      .catch((error) => {
        console.error(error);
        return false;
      });
    console.log('Engine Save to levelDB => ', data);
  } else {
    const tx = main.db?.transaction([dbName], 'readwrite');
    if (!tx) {
      return false;
    }
    const table = tx.objectStore(dbName);

    console.log('Engine Save to indexedDB => ', data);
    table.put({
      id: dataId,
      data: JSON.stringify(data),
      timestamp,
    });

    return true;
  }
};

const readData = async ({ dataId, dbName, parse = false }) => {
  let data;
  if (window.APP_TRON) {
    data = await window.APP_TRON.readLevelDBData(dataId, dbName);
    if (!data) {
      return undefined;
    }
  } else {
    const tx = main.db?.transaction([dbName], 'readwrite');
    if (!tx) {
      return false;
    }
    const table = tx.objectStore(dbName);

    const getReq = table.get(dataId);
    data = await sneakyPromise(
      resolve => getReq.addEventListener('success', () => {
        resolve(getReq.result);
      },
      {
        once: true,
      }),
    );
    if (!data) {
      return undefined;
    }
  }
  return parse ? JSON.parse(data.data) : data;
};

const cleanupExpiredData = async ({ dbName }) => {
  let cleanedCount = 0;
  
  if (window.APP_TRON) {
    // For LevelDB (Electron app)
    try {
      await window.APP_TRON.cleanupLevelDBData({
        dbName,
        now: Date.now(),
      });
      
    } catch (error) {
      console.error('[CACHE] Error during LevelDB cleanup:', error);
    }
  } else {
    // For IndexedDB (browser)
    return new Promise((resolve, reject) => {
      if (!main.db) {
        console.warn('IndexedDB not initialized yet, skipping cleanup for:', dbName);
        resolve(0);
        return;
      }
      
      const tx = main.db.transaction([dbName], 'readwrite');
      console.log('Cleaning up expired data in IndexedDB:', dbName, 'tx status', tx ? 'active' : 'not active');
      if (!tx) {
        console.error('Failed to create transaction for:', dbName);
        resolve(0);
        return;
      }
      
      const table = tx.objectStore(dbName);
      const getAllRequest = table.getAll();
      
      console.log('Cleaning up expired data in IndexedDB:', dbName);
      getAllRequest.onsuccess = () => {
        const allRecords = getAllRequest.result;
        const now = Date.now();
        
        const deletePromises = allRecords
          .filter(record => {
            return record.timestamp && record.timestamp < now;
          })
          .map(record => {
            return new Promise((resolveDelete) => {
              const deleteRequest = table.delete(record.id);
              deleteRequest.onsuccess = () => {
                cleanedCount++;
                resolveDelete();
              };
              deleteRequest.onerror = () => resolveDelete();
            });
          });
        
        Promise.all(deletePromises).then(() => {
          resolve(cleanedCount);
        }).catch(() => resolve(cleanedCount));
      };
      
      getAllRequest.onerror = () => reject(getAllRequest.error);
    });
  }
  
  return cleanedCount;
};

const init = async () => {
  let savedRequestsData;
  if (window.APP_TRON) {
    const sizeWindowData = await readData({
      dataId: 'size-window',
      dbName: 'commonSpace',
    });

    const maximizeWindowData = await readData({
      dataId: 'maximize-window',
      dbName: 'commonSpace',
    });

    let sizeWindow;
    let maximizeWindow;

    if (sizeWindowData) {
      sizeWindow = JSON.parse(sizeWindowData.data);
    }
    if (maximizeWindowData) {
      maximizeWindow = JSON.parse(maximizeWindowData.data);
    }
    if (maximizeWindow) {
      ipcRenderer.send('mainMaximize');
    } else if (sizeWindow) {
      ipcRenderer.send('resize-window', sizeWindow[0], sizeWindow[1]);
    }

    savedRequestsData = await readData({
      dataId: 'requests',
      dbName: 'offlineRequests',
    });
  } else {
    const idbReq = indexedDB.open('tracklify', 1);
    idbReq.addEventListener('upgradeneeded', () => {
      main.db = idbReq.result;
      main.db.createObjectStore('offlineRequests', {
        keyPath: 'id',
      });
      main.db.createObjectStore('commonSpace', {
        keyPath: 'id',
      });
    });
    idbReq.addEventListener('success', async () => {
      main.db = idbReq.result;
      console.log('🐕‍🦺 IDB open success');
      savedRequestsData = await readData({
        dataId: 'requests',
        dbName: 'offlineRequests',
      });
    });
  }

  if (savedRequestsData) {
    const requests = JSON.parse(savedRequestsData.data);
    initPersistedCalls(requests);
  }
};

export default {
  init,
  saveData,
  readData,
  cleanupExpiredData,
};
